import { Col, Row, Typography } from 'antd'
import React, { useEffect, useState } from 'react'
// If your app already imports Ant Design styles globally, you can remove this line.
import 'antd/dist/antd.css'

const { Text, Title } = Typography

/**
 * Design goals
 * - No inputs. Pure marketing/overview + CTA.
 * - Polished visual language with consistent spacing, shadows, gradients.
 * - Fully responsive (mobile-first).
 * - Minimal inline style where necessary; lightweight CSS-in-JS for animations.
 */

const Circle: React.FC<{ num: number; large: boolean }> = ({ num, large }) => {
  const size = large ? 56 : 42
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg,#1890FF,#40a9ff)',
        boxShadow: '0 8px 18px rgba(24,144,255,.35)',
        border: '2px solid rgba(255,255,255,.15)',
        flexShrink: 0,
        marginRight: large ? 16 : 12,
      }}
      aria-hidden
    >
      <Title level={large ? 3 : 4} style={{ color: '#fff', margin: 0, fontWeight: 700 }}>
        {num}
      </Title>
    </div>
  )
}

const StepRow: React.FC<{
  num: number
  large: boolean
  message: React.ReactNode
  isLast?: boolean
}> = ({ num, large, message, isLast }) => {
  return (
    <div style={{ position: 'relative', paddingLeft: large ? 4 : 2 }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Circle num={num} large={large} />
        <Text
          style={{
            fontSize: large ? 18 : 16,
            lineHeight: 1.5,
            color: '#fff',
            fontWeight: 500,
            textShadow: '0 1px 2px rgba(0,0,0,.25)',
          }}
        >
          {message}
        </Text>
      </div>
      {!isLast && (
        <div
          style={{
            marginLeft: large ? 28 : 22,
            height: large ? 56 : 44,
            width: 3,
            borderRadius: 2,
            background: 'linear-gradient(180deg, rgba(255,255,255,.25), rgba(24,144,255,.65))',
            boxShadow: '0 2px 8px rgba(24,144,255,.35)',
          }}
        />
      )}
    </div>
  )
}

const DecorativeBlob: React.FC<{
  size?: number
  opacity?: number
  style?: React.CSSProperties
}> = ({ size = 140, opacity = 0.08, style }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 80 80"
    style={{ opacity, ...style }}
    className="float-blob"
  >
    <defs>
      <linearGradient id="blobGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#fff" />
        <stop offset="100%" stopColor="#e6f7ff" />
      </linearGradient>
    </defs>
    <path
      d="M17.5 77.4c-4.3 0-8.6-1.6-11.8-4.8C-1 66.1-1 55.7 5.5 49.3L50.6 4.8c6.5-6.4 17-6.4 23.6 0 6.5 6.4 6.5 16.8 0 23.3L29.3 72.6c-3.2 3.2-7.6 4.8-11.8 4.8Z"
      fill="url(#blobGrad)"
    />
  </svg>
)

const useDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 992px)').matches : true,
  )
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 992px)')
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return isDesktop
}

const HomePageCheckin: React.FC = () => {
  const isDesktop = useDesktop()

  const steps = [
    {
      num: 1,
      message: (
        <>
          Purchase your reservation on <strong style={{ color: '#FFD666' }}>Trip.com</strong>
        </>
      ),
    },
    { num: 2, message: 'Receive check-in link via email' },
    { num: 3, message: 'Complete your online check-in process' },
    { num: 4, message: 'Show your QR code to admin at the Pick Up Station' },
  ]

  return (
    <div
      style={{
        minHeight: '100vh',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #003B8E 0%, #002B6A 50%, #006AD8 100%)',
      }}
    >
      {/* Local styles */}
      <style>{`
        @keyframes riseIn { from { opacity: 0; transform: translateY(24px) } to { opacity: 1; transform: translateY(0) } }
        @keyframes float { 0%,100% { transform: translateY(0px) } 50% { transform: translateY(-10px) } }
        .float-blob { animation: float 8s ease-in-out infinite; }
        .hero-cta { transition: transform .25s ease, box-shadow .25s ease }
        .hero-cta:hover { transform: translateY(-2px); box-shadow: 0 14px 34px rgba(24,144,255,.45) }
        .glass {
          background: linear-gradient(180deg, rgba(255,255,255,.08), rgba(255,255,255,.03));
          border: 1px solid rgba(255,255,255,.1);
          backdrop-filter: blur(10px);
        }
      `}</style>

      <Row
        gutter={[0, 0]}
        align="stretch"
        justify="center"
        style={{ minHeight: '100vh', position: 'relative' }}
      >
        {/* Decorations */}
        <DecorativeBlob
          size={180}
          opacity={0.06}
          style={{ position: 'absolute', top: 24, left: isDesktop ? 24 : -40 }}
        />
        <DecorativeBlob
          size={140}
          opacity={0.06}
          style={{ position: 'absolute', bottom: 24, right: isDesktop ? 40 : -30 }}
        />

        {/* Left: Hero */}
        <Col xs={24} lg={12} style={{ display: 'flex' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              width: '100%',
              padding: isDesktop ? '64px 48px 64px 64px' : '32px 20px',
            }}
          >
            {/* Brand Card */}
            <div
              className="glass"
              style={{
                borderRadius: isDesktop ? 28 : 22,
                padding: isDesktop ? '28px 32px' : '18px 20px',
                boxShadow: '0 16px 42px rgba(0,0,0,.28)',
                animation: 'riseIn .6s ease both',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {/* Simple mark */}
                <div
                  style={{
                    width: isDesktop ? 44 : 34,
                    height: isDesktop ? 44 : 34,
                    borderRadius: 12,
                    background: 'linear-gradient(135deg,#1890FF,#40a9ff)',
                    boxShadow: '0 8px 18px rgba(24,144,255,.45)',
                  }}
                />
                <div
                  style={{
                    height: isDesktop ? 40 : 30,
                    width: 2,
                    background: 'linear-gradient(#fff,#e6f7ff)',
                    opacity: 0.85,
                    borderRadius: 2,
                  }}
                />
                <div>
                  <Text
                    style={{
                      fontSize: isDesktop ? 14 : 12,
                      color: 'rgba(255,255,255,.75)',
                      letterSpacing: 1.2,
                    }}
                  >
                    H A U P C A R
                  </Text>
                  <Title level={isDesktop ? 2 : 3} style={{ margin: 0, color: '#fff' }}>
                    Online Car Rental Check‑in
                  </Title>
                  <Text style={{ color: 'rgba(255,255,255,.9)' }}>
                    Streamlined • Secure • Simple
                  </Text>
                </div>
              </div>
            </div>

            {/* Lead copy */}
            <div style={{ marginTop: isDesktop ? 28 : 20 }}>
              <Text
                style={{
                  color: 'rgba(255,255,255,.95)',
                  fontSize: isDesktop ? 16 : 15,
                  lineHeight: 1.7,
                }}
              >
                Welcome to our fast, secure check‑in experience. Follow four quick steps and you’ll
                be on the road in minutes.
              </Text>
            </div>
          </div>
        </Col>

        {/* Right: Steps */}
        <Col xs={24} lg={12} style={{ display: 'flex' }}>
          <div
            style={{
              width: '100%',
              margin: isDesktop ? '40px 32px 40px 0' : '0',
              borderTopLeftRadius: isDesktop ? 24 : 28,
              borderTopRightRadius: isDesktop ? 24 : 28,
              borderBottomLeftRadius: isDesktop ? 24 : 0,
              borderBottomRightRadius: isDesktop ? 24 : 0,
              background: 'linear-gradient(180deg, rgba(0,0,0,.28), rgba(0,0,0,.18))',
              boxShadow: isDesktop ? '0 22px 48px rgba(0,0,0,.35)' : '0 -14px 34px rgba(0,0,0,.35)',
              padding: isDesktop ? '48px 32px 56px 32px' : '28px 18px 36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* radial accents */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backgroundImage:
                  'radial-gradient(circle at 25% 20%, rgba(255,255,255,.06) 0%, transparent 60%), radial-gradient(circle at 75% 80%, rgba(24,144,255,.12) 0%, transparent 55%)',
              }}
            />

            <div style={{ width: '92%', maxWidth: 640, position: 'relative', zIndex: 2 }}>
              <div style={{ marginBottom: isDesktop ? 24 : 16 }}>
                <Text
                  style={{
                    display: 'block',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: isDesktop ? 36 : 24,
                    textShadow: '0 2px 8px rgba(0,0,0,.4)',
                  }}
                >
                  Simple Steps
                </Text>
                <Text style={{ color: 'rgba(255,255,255,.8)', fontSize: isDesktop ? 16 : 14 }}>
                  Follow these quick steps to complete your check‑in
                </Text>
              </div>

              <div style={{ display: 'grid', gap: isDesktop ? 18 : 14 }}>
                {steps.map((s, idx) => (
                  <div
                    key={s.num}
                    style={{ animation: `riseIn .6s ease ${(idx + 1) * 80}ms both` }}
                  >
                    <StepRow
                      num={s.num}
                      large={isDesktop}
                      message={s.message}
                      isLast={idx === steps.length - 1}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  )
}

export default HomePageCheckin
